// Central catalog of dropdown data.
// Categories are defined ONCE below; API_CATEGORIES and CATEGORY_LABEL are derived
// from those definitions so the two vocabularies (UI / API) can never drift apart.

// ---------------------------------------------------------------------------
// Top-level UI categories (customer-facing) — 4 product lines
// ---------------------------------------------------------------------------
// `comingSoon` product lines stay visible and selectable, but show
// COMING_SOON_NOTE instead of a product list.
export const CATEGORIES = [
  { value: "batteries", label: "Batteries" },
  { value: "hups", label: "HUPS" },
  { value: "lubricants", label: "Lubricants" },
  { value: "solar", label: "Solar Product Solution", comingSoon: true },
];

export const isCategoryComingSoon = (category) =>
  !!CATEGORIES.find((c) => c.value === category)?.comingSoon;

export const COMING_SOON_CATEGORIES = CATEGORIES.filter((c) => c.comingSoon).map(
  (c) => c.value
);

// ---------------------------------------------------------------------------
// Sub-types under Batteries — 6 customer-facing groups.
//   label     — shown to the customer in the subtype dropdown
//   apiLabel  — shown in the admin filter (defaults to `${label} Battery`)
//   fuels     — fuel options offered for this subtype
//   sheet     — source tab in the Amaron fitment chart workbook
//   dynamic   — models come from the chart cascade (make -> model -> battery)
//   groups    — third level, only where one subtype covers several chart sheets
// ---------------------------------------------------------------------------
export const BATTERY_SUBTYPES = [
  {
    value: "two_wheeler",
    // Electric two-wheelers are listed but Amaron has not launched a battery
    // for them yet — shown as coming soon rather than returning no results.
    fuels: ["petrol", "electric"],
    comingSoon: ["electric"],
    label: "Two Wheeler",
    apiLabel: "Two Wheeler Battery",
    sheet: "Two Wheelers",
    dynamic: true,
  },
  {
    value: "automotive",
    fuels: ["petrol", "diesel", "cng", "electric"],
    label: "Automotive (Car / SUV)",
    apiLabel: "Automotive Battery",
    sheet: "Passenger Vehicles",
    dynamic: true,
  },
  {
    // The fitment chart covers IC three-wheelers only (Diesel / CNG / LPG /
    // Petrol). E-rickshaws have no chart rows, so they are picked directly
    // from THREE_WHEELER_MODELS instead of cascading make -> model.
    value: "three_wheeler",
    fuels: ["petrol", "diesel", "cng", "electric"],
    label: "Three Wheeler",
    apiLabel: "Three Wheeler Battery",
    dynamic: true,
    groups: [
      { value: "three_wheeler_ic", label: "Petrol / Diesel / CNG", sheet: "Three Wheelers" },
      { value: "e_rickshaw", label: "E-Rickshaw (Electric)", direct: true },
    ],
  },
  {
    value: "tractor",
    fuels: ["diesel"],
    label: "Tractor",
    apiLabel: "Tractor Battery",
    sheet: "Tractors",
    dynamic: true,
  },
  {
    value: "commercial",
    fuels: ["diesel"],
    label: "Truck / Bus / LCV",
    apiLabel: "Truck / Bus / LCV Battery",
    sheet: "Commercial Vehicles",
    dynamic: true,
  },
  {
    // Two chart sheets share this subtype: SPVs cascade make -> model,
    // gensets are picked by KVA rating instead, so they stay separable.
    value: "spv_genset",
    fuels: ["diesel"],
    label: "Construction / SPV & Genset / DG Set",
    apiLabel: "Construction / SPV & Genset Battery",
    dynamic: true,
    groups: [
      { value: "special_purpose", label: "Construction / SPV", sheet: "Special Purpose Vehicles" },
      { value: "genset", label: "Genset / DG Set", sheet: "Gensets", ratedBy: "kva" },
    ],
  },
];

const subtypeApiLabel = (s) => s.apiLabel || `${s.label} Battery`;

// ---------------------------------------------------------------------------
// Fuel options offered per battery subtype. Drives the fuel dropdown; the
// chart cascade then filters models on the matching Fuel column.
// ---------------------------------------------------------------------------
export const FUEL_LABEL = {
  petrol: "Petrol",
  diesel: "Diesel",
  cng: "CNG",
  electric: "Electric",
};

// Fuels flagged `comingSoon` have no Amaron battery yet. Keep them selectable
// so the range looks complete, but show COMING_SOON_NOTE instead of an empty
// result list.
export const fuelsFor = (subtype) => {
  const s = BATTERY_SUBTYPES.find((x) => x.value === subtype);
  if (!s) return [];
  const soon = new Set(s.comingSoon || []);
  return (s.fuels || []).map((f) => ({
    value: f,
    label: FUEL_LABEL[f],
    comingSoon: soon.has(f),
  }));
};

export const isComingSoon = (subtype, fuel) =>
  (BATTERY_SUBTYPES.find((s) => s.value === subtype)?.comingSoon || []).includes(fuel);

// Generic — covers both coming-soon product lines and coming-soon fuel types.
export const COMING_SOON_NOTE =
  "This range has not launched yet. Contact us and we will let you know as soon as it is available.";

// ---------------------------------------------------------------------------
// Amaron series, distinguished by warranty.
//   fr — free-replacement months
//   pr — pro-rata months
// Deliberately no pricing here: dealer prices are internal, not customer-facing.
// ---------------------------------------------------------------------------
export const BATTERY_SERIES = [
  { value: "black", label: "Amaron Black", fr: 18, pr: 18 },
  { value: "go", label: "Amaron GO", fr: 30, pr: 30 },
  { value: "flo", label: "Amaron FLO", fr: 36, pr: 36 },
  // DURA and JADE are the same series (JADE is the AGM variant, code AAM-JD).
  { value: "dura", label: "Amaron DURA & JADE", fr: 24, pr: 24 },
  { value: "pro", label: "Amaron PRO", fr: 36, pr: 30 },
  { value: "beta", label: "Amaron BETA", fr: 24, pr: 24 },
  { value: "harvest", label: "Amaron Harvest", fr: 24, pr: 18 },
  { value: "hiway", label: "Amaron Hi-Way", fr: 24, pr: 24 },
  // TODO: confirm E-Rick warranty months (traction range, code AAM-ER).
  { value: "erick", label: "Amaron E-Rick", fr: null, pr: null },
];

// "36 FR + 30 PR months warranty*" / "12 FR months warranty*" when pr is 0.
// Returns "" while a series has no confirmed warranty, so the UI shows nothing
// rather than "null FR months".
export const seriesWarranty = (s) => {
  if (s.fr == null) return "";
  return s.pr ? `${s.fr} FR + ${s.pr} PR months warranty*` : `${s.fr} FR months warranty*`;
};

export const SERIES_LABEL = Object.fromEntries(
  BATTERY_SERIES.map((s) => [s.value, s.label])
);
export const SERIES_WARRANTY = Object.fromEntries(
  BATTERY_SERIES.map((s) => [s.value, seriesWarranty(s)])
);

// FR = free replacement, PR = pro-rata. Show once alongside any warranty text.
export const WARRANTY_FOOTNOTE =
  "FR = Free Replacement. PR = Pro-Rata basis. Terms apply.";

// Model codes carry their series in the first two segments — note ABR-PR is
// the two-wheeler BETA range, NOT Amaron PRO. Prefix beats sheet section
// headers, which are unreliable in the dealer price list.
export const SERIES_BY_CODE_PREFIX = {
  "AAM-BL": "black",
  "AAM-FL": "flo",
  "AAM-GO": "go",
  "AAM-PR": "pro",
  "AAM-HW": "hiway",
  "AAM-HR": "harvest",
  "AAM-DR": "dura",
  "AAM-BA": "beta",
  "ABR-PR": "beta",
  "AAM-JD": "dura",
  "AAM-ER": "erick",
};

export const seriesForCode = (code) =>
  code ? SERIES_BY_CODE_PREFIX[code.split("-").slice(0, 2).join("-")] || "" : "";

// Third-level options, looked up by their parent subtype value.
export const subtypeGroups = (subtype) =>
  BATTERY_SUBTYPES.find((s) => s.value === subtype)?.groups || [];

export const THREE_WHEELER_GROUPS = subtypeGroups("three_wheeler");
export const SPV_GENSET_GROUPS = subtypeGroups("spv_genset");

// Every group across all subtypes, flattened.
const ALL_GROUPS = BATTERY_SUBTYPES.flatMap((s) =>
  (s.groups || []).map((g) => ({ ...g, subtype: s.value }))
);

// Values that drive the Amaron chart cascade. Groups flagged `direct: true`
// are excluded — they have no chart rows and are picked from a model list.
export const VEHICLE_SEGMENTS = new Set(
  BATTERY_SUBTYPES.filter((s) => s.dynamic).flatMap((s) =>
    s.groups ? s.groups.filter((g) => !g.direct).map((g) => g.value) : [s.value]
  )
);

// ---------------------------------------------------------------------------
// Legacy values accepted on input only — never shown in dropdowns.
// Each maps to { subtype, group } in the current 3-category structure.
//   passenger_vehicles — old slug for what is now "automotive"
//   inverter / hups_batteries — tubular batteries, now under the HUPS line
// ---------------------------------------------------------------------------
export const CATEGORY_ALIASES = {
  passenger_vehicles: { category: "batteries", subtype: "automotive" },
  inverter: { category: "hups", subtype: "tubular_batteries" },
  hups_batteries: { category: "hups", subtype: "tubular_batteries" },
  ...Object.fromEntries(
    ALL_GROUPS.map((g) => [
      g.value,
      { category: "batteries", subtype: g.subtype, group: g.value },
    ])
  ),
};

// ---------------------------------------------------------------------------
// Sub-types under the HUPS product line. Tubular batteries live here now —
// they are sold as the battery half of a home-inverter system, not as a
// vehicle battery, so they no longer sit under Batteries.
// ---------------------------------------------------------------------------
export const HUPS_SUBTYPES = [
  { value: "ups_units", label: "Home UPS / Inverter" },
  { value: "tubular_batteries", label: "Inverter Battery (Tubular)" },
];

// ---------------------------------------------------------------------------
// All API-level category values (used by the admin filter).
// Deliberately finer-grained than the UI: admins filter by chart segment.
// ---------------------------------------------------------------------------
export const API_CATEGORIES = [
  ...BATTERY_SUBTYPES.filter((s) => !s.groups).map((s) => ({
    value: s.value,
    label: subtypeApiLabel(s),
  })),
  ...ALL_GROUPS.map((g) => ({ value: g.value, label: `${g.label} Battery` })),
  ...HUPS_SUBTYPES.map((s) => ({ value: s.value, label: s.label })),
  { value: "lubricants", label: "Lubricants" },
  { value: "solar", label: "Solar Product Solution" },
];

// Display lookup for any category, subtype, group or legacy value.
export const CATEGORY_LABEL = {
  ...Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label])),
  ...Object.fromEntries(BATTERY_SUBTYPES.map((s) => [s.value, subtypeApiLabel(s)])),
  ...Object.fromEntries(ALL_GROUPS.map((g) => [g.value, `${g.label} Battery`])),
  ...Object.fromEntries(HUPS_SUBTYPES.map((s) => [s.value, s.label])),
  passenger_vehicles: "Automotive Battery",
  inverter: "Inverter Battery (Tubular)",
  hups_batteries: "Inverter Battery (Tubular)",
};

// Convert an API category value to UI category + subtype (+ group where nested).
const BATTERY_SUBTYPE_VALUES = new Set(BATTERY_SUBTYPES.map((s) => s.value));
const HUPS_SUBTYPE_VALUES = new Set(HUPS_SUBTYPES.map((s) => s.value));
export const apiToUi = (api) => {
  if (!api) return { category: "", subtype: "", group: "" };
  const alias = CATEGORY_ALIASES[api];
  if (alias) return { group: "", ...alias };
  if (BATTERY_SUBTYPE_VALUES.has(api)) return { category: "batteries", subtype: api, group: "" };
  if (HUPS_SUBTYPE_VALUES.has(api)) return { category: "hups", subtype: api, group: "" };
  return { category: api, subtype: "", group: "" };
};

// ---------------------------------------------------------------------------
// Model codes from the Amaron dealer list, series resolved by code prefix.
// Prices are intentionally omitted - dealer pricing is internal.
// ---------------------------------------------------------------------------
export const TWO_WHEELER_MODELS = [
  { code: "AAM-BA-0A48ATZ6L", series: "beta" },
  { code: "AAM-BA-A48ATZ14R", series: "beta" },
  { code: "ABR-PR-12APBTX25", series: "beta" },
  { code: "ABR-PR-12APBTX50", series: "beta" },
  { code: "ABR-PR-12APBTX7R", series: "beta" },
  { code: "ABR-PR-12APBTX90", series: "beta" },
  { code: "ABR-PR-APBTZ4L", series: "beta" },
  { code: "ABR-PR-APBTZ5L", series: "beta" },
  { code: "ABR-PR-APBTZ7L", series: "beta" },
  { code: "ABR-PR-APBTZ9R", series: "beta" },
  { code: "ABR-PR-HMATZ4L", series: "beta" },
  { code: "ABR-PR-HMATZ5L", series: "beta" },
];

// E-Rickshaw battery — listed on the dealer Tubular sheet, but it is a
// three-wheeler traction battery, so it belongs to that subtype's
// "e_rickshaw" group. Picked directly; it has no fitment-chart rows.
export const THREE_WHEELER_MODELS = [
  { code: "AAM-ER-ER2200T12", series: "erick", group: "e_rickshaw" },
];

export const AUTOMOTIVE_MODELS = [
  { code: "AAM-BL-0BL400RMF", series: "black" },
  { code: "AAM-PR-00050B20L", series: "pro" },
  { code: "AAM-BL-BL00500RS", series: "black" },
  { code: "AAM-PR-00050B20R", series: "pro" },
  { code: "AAM-BL-BL00500LS", series: "black" },
  { code: "AAM-PR-0055B24LS", series: "pro" },
  { code: "AAM-BL-0BL700LMF", series: "black" },
  { code: "AAM-PR-574102069", series: "pro" },
  { code: "AAM-BL-0BL700RMF", series: "black" },
  { code: "AAM-PR-600109087", series: "pro" },
  { code: "AAM-BL-0BL800LMF", series: "black" },
  { code: "AAM-PR-0BH80D31L", series: "pro" },
  { code: "AAM-BL-0BL800RMF", series: "black" },
  { code: "AAM-BL-BL880D31R", series: "black" },
  { code: "AAM-FL-00040B20L", series: "flo" },
  { code: "AAM-BL-BL880D31L", series: "black" },
  { code: "AAM-FL-00040B20R", series: "flo" },
  { code: "AAM-BL-0BL900LMF", series: "black" },
  { code: "AAM-FL-00042B20L", series: "flo" },
  { code: "AAM-BL-0BL900RMF", series: "black" },
  { code: "AAM-FL-00042B20R", series: "flo" },
  { code: "AAM-BL-BL1000LMF", series: "black" },
  { code: "AAM-FL-0BH40B20L", series: "flo" },
  { code: "AAM-BL-BL1000RMF", series: "black" },
  { code: "AAM-FL-0BH45D20L", series: "flo" },
  { code: "AAM-BL-BL1300RMF", series: "black" },
  { code: "AAM-FL-0BH90D23L", series: "flo" },
  { code: "AAM-BL-BL1500RMF", series: "black" },
  { code: "AAM-FL-545106036", series: "flo" },
  { code: "AAM-BL-0BL600LMF", series: "black" },
  { code: "AAM-FL-555112054", series: "flo" },
  { code: "AAM-BL-0BL600RMF", series: "black" },
  { code: "AAM-FL-566112060", series: "flo" },
  { code: "AAM-FL-00080D23L", series: "flo" },
  { code: "AAM-BL-BL0030RMF", series: "black" },
  { code: "AAM-FL-550113042", series: "flo" },
  { code: "AAM-BL-BL0040LMF", series: "black" },
  { code: "AAM-FL-550114042", series: "flo" },
  { code: "AAM-BL-BL0040RMF", series: "black" },
  { code: "AAM-FL-555111054", series: "flo" },
  { code: "AAM-BL-0BL0050LS", series: "black" },
  { code: "AAM-FL-565106590", series: "flo" },
  { code: "AAM-BL-0BL0050RS", series: "black" },
  { code: "AAM-FL-580112073", series: "flo" },
  { code: "AAM-BL-BL0060LMF", series: "black" },
  { code: "AAM-BL-BL0060RMF", series: "black" },
  { code: "AAM-BL-BL0070LMF", series: "black" },
  { code: "AAM-BL-BL0070RMF", series: "black" },
  { code: "AAM-BL-BL0080LMF", series: "black" },
  { code: "AAM-BL-BL0080RMF", series: "black" },
  { code: "AAM-BL-BL0090LMF", series: "black" },
  { code: "AAM-GO-00034B20L", series: "go" },
  { code: "AAM-BL-BL0090RMF", series: "black" },
  { code: "AAM-GO-00034B20R", series: "go" },
  { code: "AAM-BL-BL090E41L", series: "black" },
  { code: "AAM-GO-00038B20L", series: "go" },
  { code: "AAM-BL-BL090E41R", series: "black" },
  { code: "AAM-GO-00038B20R", series: "go" },
  { code: "AAM-BL-BL100E41L", series: "black" },
  { code: "AAM-GO-0BH38B20R", series: "go" },
  { code: "AAM-BL-BL100E41R", series: "black" },
  { code: "AAM-GO-00050B24L", series: "go" },
  { code: "AAM-BL-0BL100LMF", series: "black" },
  { code: "AAM-GO-00095D26L", series: "go" },
  { code: "AAM-BL-0BL100RMF", series: "black" },
  { code: "AAM-GO-00095D26R", series: "go" },
  { code: "AAM-BL-0BL130RMF", series: "black" },
  { code: "AAM-GO-565106590", series: "go" },
  { code: "AAM-BL-0BL150RMF", series: "black" },
  { code: "AAM-GO-00085D23R", series: "go" },
  { code: "AAM-GO-00105D26R", series: "go" },
  { code: "AAM-HW-HC620D31R", series: "hiway" },
  { code: "AAM-GO-00105D26L", series: "go" },
  { code: "AAM-HW-NT650H29R", series: "hiway" },
  { code: "AAM-GO-00105D31R", series: "go" },
  { code: "AAM-HW-NT800D04R", series: "hiway" },
  { code: "AAM-GO-00105D31L", series: "go" },
  { code: "AAM-HW-NTX00D04R", series: "hiway" },
  { code: "AAM-GO-00135D31R", series: "go" },
  { code: "AAM-HW-HC180D04R", series: "hiway" },
  { code: "AAM-GO-00135D31L", series: "go" },
  { code: "AAM-HW-HCX20H52R", series: "hiway" },
  { code: "AAM-HW-NT800E41R", series: "hiway" },
  { code: "AAM-DR-EFB60B20L", series: "dura" },
  { code: "AAM-HW-NT700E41R", series: "hiway" },
  { code: "AAM-DR-EFB80B24L", series: "dura" },
  { code: "AAM-HW-NT800F51R", series: "hiway" },
  { code: "AAM-DR-EFBDIN70L", series: "dura" },
  { code: "AAM-DR-EF100D23R", series: "dura" },
  { code: "AAM-HR-TR500D31L", series: "harvest" },
  { code: "AAM-DR-EFBDIN47R", series: "dura" },
  { code: "AAM-HR-TR500D31R", series: "harvest" },
  { code: "AAM-JD-AGMDIN50L", series: "dura" },
  { code: "AAM-HR-NT600H29L", series: "harvest" },
  { code: "AAM-DR-EFBDIN52R", series: "dura" },
  { code: "AAM-HR-NT600H29R", series: "harvest" },
  { code: "AAM-HR-NT600E41R", series: "harvest" },
  { code: "AAM-BL-BL0300RMF", series: "black" },
  { code: "AAM-HR-NT600E41L", series: "harvest" },
  { code: "AAM-BL-0BL400LMF", series: "black" },
];

// ---------------------------------------------------------------------------
// Inverter Battery (Tubular) — 21 Amaron tubular models.
// Belongs to the HUPS product line (subtype "tubular_batteries"), not Batteries.
// ---------------------------------------------------------------------------
export const TUBULAR_MODELS = [
  { code: "AAM-CR-AR150TT54", series: "AR — Tall Tubular", ah: 150 },
  { code: "AAM-CR-AR150TT60", series: "AR — Tall Tubular", ah: 150 },
  { code: "AAM-CR-AR150TN54", series: "AR — Tubular", ah: 150 },
  { code: "AAM-CR-AR200TT54", series: "AR — Tall Tubular", ah: 200 },
  { code: "AAM-CR-AR200TT60", series: "AR — Tall Tubular", ah: 200 },
  { code: "AAM-CR-AR230TT54", series: "AR — Tall Tubular", ah: 230 },
  { code: "AAM-CR-AM150TT42", series: "AM — Tall Tubular", ah: 150 },
  { code: "AAM-CR-AM150TT54", series: "AM — Tall Tubular", ah: 150 },
  { code: "AAM-CR-AM200TT54", series: "AM — Tall Tubular", ah: 200 },
  { code: "AAM-CR-AM230TT54", series: "AM — Tall Tubular", ah: 230 },
  { code: "AAM-CR-AM180TT54", series: "AM — Tall Tubular", ah: 180 },
  { code: "AAM-CR-AM250TT54", series: "AM — Tall Tubular", ah: 250 },
  { code: "AAM-CR-AM130ST48", series: "AM — Short Tubular", ah: 130 },
  { code: "AAM-CR-AM150ST48", series: "AM — Short Tubular", ah: 150 },
  { code: "AAM-CR-AM165ST48", series: "AM — Short Tubular", ah: 165 },
  { code: "AAM-CR-AM180JT48", series: "AM — Jumbo Tubular", ah: 180 },
  { code: "AAM-CR-AMS110T72", series: "AMS — Tubular", ah: 110 },
  { code: "AAM-CR-AMS160T72", series: "AMS — Tubular", ah: 160 },
  { code: "AAM-CR-AM130ST36", series: "AM — Short Tubular", ah: 130 },
  { code: "AAM-CR-AM145ST36", series: "AM — Short Tubular", ah: 145 },
  { code: "AAM-CR-AM160ST36", series: "AM — Short Tubular", ah: 160 },
];

// ---------------------------------------------------------------------------
// HUPS — 14 Amaron models in 2 applications (from dealer sheet).
//   Home UPS       — HE series and HB (AP) series
//   Commercial UPS — HBP series
// Keyed by slug so the value is safe for URLs, form values and DB columns;
// use HUPS_APPLICATION_LABEL (or HUPS_APPLICATIONS) for display text.
// ---------------------------------------------------------------------------
export const HUPS_MODELS = {
  home_ups: [
    { code: "AAM-HU-HE0000850", va: "850 VA", series: "HE" },
    { code: "AAM-HU-HE0001100", va: "1100 VA", series: "HE" },
    { code: "AAM-HU-HE0001300", va: "1300 VA", series: "HE" },
    { code: "AAM-HU-HE0001750", va: "1750 VA", series: "HE" },
    { code: "AAM-HU-HB00850AP", va: "850 VA", series: "HB (AP)" },
    { code: "AAM-HU-HB01000AP", va: "1000 VA", series: "HB (AP)" },
    { code: "AAM-HU-HB01200AP", va: "1200 VA", series: "HB (AP)" },
    { code: "AAM-HU-HB01300AP", va: "1300 VA", series: "HB (AP)" },
    { code: "AAM-HU-HB01600AP", va: "1600 VA", series: "HB (AP)" },
    { code: "AAM-HU-HB01850AP", va: "1850 VA", series: "HB (AP)" },
  ],
  commercial_ups: [
    { code: "AAM-HU-HBP002500", va: "2500 VA", series: "HBP" },
    { code: "AAM-HU-HBP003500", va: "3500 VA", series: "HBP" },
    { code: "AAM-HU-HBP004000", va: "4000 VA", series: "HBP" },
    { code: "AAM-HU-HBP006000", va: "6000 VA", series: "HBP" },
  ],
};
// Display text for each HUPS application slug.
export const HUPS_APPLICATION_LABEL = {
  home_ups: "Home UPS",
  commercial_ups: "Commercial UPS",
};

// Dropdown options — derived, so the slug list can never drift from the models.
export const HUPS_APPLICATIONS = Object.keys(HUPS_MODELS).map((value) => ({
  value,
  label: HUPS_APPLICATION_LABEL[value] || value,
}));

// Legacy values accepted on input only (old rows / saved enquiries):
// the previous display-string keys, plus the retired "Compact / Economy UPS"
// application whose HE models now sit under Home UPS.
const HUPS_APPLICATION_ALIASES = {
  ...Object.fromEntries(
    Object.entries(HUPS_APPLICATION_LABEL).map(([slug, label]) => [label, slug])
  ),
  compact_ups: "home_ups",
  "Compact / Economy UPS": "home_ups",
};

// Resolve any HUPS application value — slug or legacy label — to its models.
export const hupsModels = (app) => {
  if (!app) return [];
  return HUPS_MODELS[HUPS_APPLICATION_ALIASES[app] || app] || [];
};

// Inverter (local flow — hardcoded because HUPS/UPS ratings are standard)
export const INVERTER_VA = ["500-700 VA", "800-1100 VA", "1200-1800 VA", "2000-3000 VA"];
export const BACKUP_HOURS = ["2-3 hours", "4-6 hours", "8-10 hours", "12+ hours"];

// ---------------------------------------------------------------------------
// Solar
// ---------------------------------------------------------------------------
export const SOLAR_USAGE = ["Home", "Shop", "Office", "Farm", "Commercial"];
export const SOLAR_LOAD = ["Under 1 KW", "1-3 KW", "3-5 KW", "5-10 KW", "10+ KW"];

// ---------------------------------------------------------------------------
// Amaron Hi-Life Lubes - browse by family. Ref AREML/AHL/FY27/AI/Q1/RTP/002,
// 21 April 2026. Prices deliberately omitted.
//   grade - viscosity, where the product is identified by one
//   name  - product name, for families not identified by viscosity
//   pack  - pack size in `unit` (L or kg)
// ---------------------------------------------------------------------------
export const LUBE_FAMILIES = [
  { value: "motorcycle_oil", label: "Motorcycle Oil" },
  { value: "car_oil", label: "Passenger Car Motor Oil" },
  { value: "three_wheeler_oil", label: "Three Wheeler Oil" },
  { value: "grease", label: "Grease" },
  { value: "diesel_engine_oil", label: "Diesel Engine Oil" },
  { value: "gear_oil", label: "Gear Oil" },
  { value: "automotive_fluid", label: "Automotive Fluid" },
  { value: "hydraulic_oil", label: "Hydraulic Oil (Hydroflo)" },
  { value: "industrial_gear_oil", label: "Industrial Gear Oil" },
  { value: "industrial_grease", label: "Industrial Grease" },
  { value: "industrial_transmission", label: "Industrial Transmission Oil" },
  { value: "industrial_turbine", label: "Industrial Turbine Oil" },
  { value: "industrial_other", label: "Other Industrial Fluids" },
];

export const LUBE_PRODUCTS = {
  motorcycle_oil: [
    { code: "AHL-PRMC-ZA0SN010W50-1000", grade: "10W-50", pack: 1, unit: "L" },
    { code: "AHL-FLMC-ZA0SN010W30-1000", grade: "10W-30", pack: 1, unit: "L" },
    { code: "AHL-FLMC-ZA0SN010W30-0900", grade: "10W-30", pack: 0.9, unit: "L" },
    { code: "AHL-FLMC-ZA0SN020W40-1000", grade: "20W-40", pack: 1, unit: "L" },
    { code: "AHL-FLMC-ZA0SN020W40-0900", grade: "20W-40", pack: 0.9, unit: "L" },
    { code: "AHL-PRMC-ZA0SN010W40-1000", grade: "10W-40", pack: 1, unit: "L" },
    { code: "AHL-FLMC-ZA0SN020W50-1200", grade: "20W-50", pack: 1.2, unit: "L" },
    { code: "AHL-FLMC-ZA0SN020W50-1000", grade: "20W-50", pack: 1, unit: "L" },
    { code: "AHL-FLMC-ZD0SN020W50-055L", grade: "20W-50", pack: 55, unit: "L" },
    { code: "AHL-FLMC-ZD0SN015W50-055L", name: "Api Sn Jaso MA2", pack: 55, unit: "L" },
    { code: "AHL-FLMC-ZA0SN015W50-2500", grade: "15W-50", pack: 2.5, unit: "L" },
    { code: "AHL-FLMC-ZA0SM020W40-1000", grade: "20W-40", pack: 1, unit: "L" },
    { code: "AHL-FLMC-ZA0SM020W40-0900", grade: "20W-40", pack: 0.9, unit: "L" },
    { code: "AHL-FLMC-ZD0SN020W40-210L", grade: "20W-40", pack: 210, unit: "L" },
    { code: "AHL-FLMC-ZD0SN010W30-210L", grade: "10W-30", pack: 210, unit: "L" },
    { code: "AHL-FLMC-ZASNMB10W30-0800", grade: "10W-30", pack: 0.8, unit: "L" },
    { code: "AHL-FLMC-ZA0SNMB5W30-0600", grade: "5W-30", pack: 0.6, unit: "L" },
    { code: "AHL-GOMC-ZA0SL020W40-1000", grade: "20W-40", pack: 1, unit: "L" },
    { code: "AHL-GOMC-ZA0SL020W40-0900", grade: "20W-40", pack: 0.9, unit: "L" },
    { code: "AHL-GOMC-ZD0SL020W40-055L", grade: "20W-40", pack: 55, unit: "L" },
    { code: "AHL-GOMC-ZD0SL020W40-210L", grade: "20W-40", pack: 210, unit: "L" },
    { code: "AHL-GOMC-ZAVSL010W30-0800", grade: "10W-30", pack: 0.8, unit: "L" },
    { code: "AHL-GOMC-10W30MB80W90-COM", name: "Combo-sl 10W-30 Mb & 80W-90", pack: 0.92, unit: "L" },
    { code: "AHL-GOMC-ZA0SL010W30-0800", grade: "10W-30", pack: 0.8, unit: "L" },
    { code: "AHL-GOMC-ZA0SL010W30-0900", grade: "10W-30", pack: 0.9, unit: "L" },
    { code: "AHL-GOMC-ZA0SL010W30-1000", grade: "10W-30", pack: 1, unit: "L" },
    { code: "AHL-GOMC-ZDSLMA10W30-055L", grade: "10W-30", pack: 55, unit: "L" },
    { code: "AHL-GOMC-ZD0SL010W30-210L", grade: "10W-30", pack: 210, unit: "L" },
    { code: "AHL-GOMC-ZAVSL010W30-1000", grade: "10W-30", pack: 1, unit: "L" },
    { code: "AHL-GOMC-ZAVSL010W30-0900", grade: "10W-30", pack: 0.9, unit: "L" },
    { code: "AHL-GOMC-ZC0SL020W40-020L", grade: "20W-40", pack: 20, unit: "L" },
    { code: "AHL-GOMC-ZAVSL020W40-1000", grade: "20W-40", pack: 1, unit: "L" },
    { code: "AHL-GOMC-ZAVSL020W40-0900", grade: "20W-40", pack: 0.9, unit: "L" },
  ],
  car_oil: [
    { code: "AHL-PRPC-ZASPC305W30-1000", grade: "5W-30", pack: 1, unit: "L" },
    { code: "AHL-PRPC-ZBSPC305W30-3000", grade: "5W-30", pack: 3, unit: "L" },
    { code: "AHL-PRPC-ZBSPC305W30-3500", grade: "5W-30", pack: 3.5, unit: "L" },
    { code: "AHL-PRPC-ZBSPC305W30-5000", grade: "5W-30", pack: 5, unit: "L" },
    { code: "AHL-PRPC-ZB0SPCF5W30-5000", grade: "5W-30", pack: 5, unit: "L" },
    { code: "AHL-PRPC-ZDSNPCF5W30-055L", grade: "5W-30", pack: 55, unit: "L" },
    { code: "AHL-PRPC-ZDSNPCF5W30-210L", grade: "5W-30", pack: 210, unit: "L" },
    { code: "AHL-PRPC-ZASNPCF5W30-1000", grade: "5W-30", pack: 1, unit: "L" },
    { code: "AHL-PRPC-ZBSNPCF5W30-3000", grade: "5W-30", pack: 3, unit: "L" },
    { code: "AHL-PRPC-ZBSNPCF5W30-3500", grade: "5W-30", pack: 3.5, unit: "L" },
    { code: "AHL-PRPC-ZBSNPCF5W30-5000", grade: "5W-30", pack: 5, unit: "L" },
    { code: "AHL-PRPC-ZA000SN0W20-1000", grade: "0W-20", pack: 1, unit: "L" },
    { code: "AHL-PRPC-ZB000SN0W20-3000", grade: "0W-20", pack: 3, unit: "L" },
    { code: "AHL-PRPC-ZB000SN0W20-3500", grade: "0W-20", pack: 3.5, unit: "L" },
    { code: "AHL-PRPC-ZB0SPCF5W30-3000", grade: "5W-30", pack: 3, unit: "L" },
    { code: "AHL-PRPC-ZB0SPCF5W30-3500", grade: "5W-30", pack: 3.5, unit: "L" },
    { code: "AHL-PRPC-ZA0SPCF5W30-1000", grade: "5W-30", pack: 1, unit: "L" },
    { code: "AHL-PRPC-ZB0SNP05W40-3500", grade: "5W-40", pack: 3.5, unit: "L" },
    { code: "AHL-PRPC-ZB0SNP05W40-4000", grade: "5W-40", pack: 4, unit: "L" },
    { code: "AHL-PRPC-ZA0SNP05W40-1000", grade: "5W-40", pack: 1, unit: "L" },
    { code: "AHL-PRPC-ZD000SN5W30-210L", name: "Sn 5W", pack: 30, unit: "L" },
    { code: "AHL-FLPC-ZBCI4P15W40-5000", grade: "15W-40", pack: 5, unit: "L" },
    { code: "AHL-FLPC-ZBCI4P15W40-4000", grade: "15W-40", pack: 4, unit: "L" },
    { code: "AHL-GOPC-ZBSLCF20W50-3000", grade: "20W-50", pack: 3, unit: "L" },
    { code: "AHL-GOPC-ZBSLCF20W50-3500", grade: "20W-50", pack: 3.5, unit: "L" },
  ],
  three_wheeler_oil: [
    { code: "AHL-GO3W-ZBCF4020W40-3500", grade: "20W-40", pack: 3.5, unit: "L" },
    { code: "AHL-GO3W-ZACF4015W40-2500", grade: "15W-40", pack: 2.5, unit: "L" },
    { code: "AHL-GO3W-ZASFCD20W50-1000", grade: "20W-50", pack: 1, unit: "L" },
  ],
  grease: [
    { code: "AHL-GRE-LITHIUM00AP3-500G", name: "Long Life Red Grease", pack: 0.5, unit: "kg" },
    { code: "AHL-GRE-LITHIUM00AP3-001K", name: "Long Life Red Grease", pack: 1, unit: "kg" },
  ],
  diesel_engine_oil: [
    { code: "AHL-PRDE-ZDCK415W40-210L", grade: "15W-40", pack: 210, unit: "L" },
    { code: "AHL-PRDE-ZCCK4015W40-022L", grade: "15W-40", pack: 22, unit: "L" },
    { code: "AHL-FLDE-ZCCI4P15W40-015L", grade: "15W-40", pack: 15, unit: "L" },
    { code: "AHL-FLDE-ZCCI4P15W40-011L", grade: "15W-40", pack: 11, unit: "L" },
    { code: "AHL-FLDE-ZCCI4P15W40-7500", grade: "15W-40", pack: 7.5, unit: "L" },
    { code: "AHL-FLDE-VCCI4P15W40-7500", grade: "15W-40", pack: 7.5, unit: "L" },
    { code: "AHL-FLDE-ZCCI4P15W40-7000", grade: "15W-40", pack: 7, unit: "L" },
    { code: "AHL-FLDE-ZCCF4V15W40-7500", grade: "15W-40", pack: 7.5, unit: "L" },
    { code: "AHL-FLDE-ZACI4P15W40-1000", grade: "15W-40", pack: 1, unit: "L" },
    { code: "AHL-FLDE-ZDCI4P15W40-055L", grade: "15W-40", pack: 55, unit: "L" },
    { code: "AHL-FLDE-ZDCI4P15W40-210L", grade: "15W-40", pack: 210, unit: "L" },
    { code: "AHL-FLDE-ZCCH4015W40-010L", grade: "15W-40", pack: 10, unit: "L" },
    { code: "AHL-FLDE-ZCCH4015W40-7500", grade: "15W-40", pack: 7.5, unit: "L" },
    { code: "AHL-FLDE-ZBCH4015W40-3000", grade: "15W-40", pack: 3, unit: "L" },
    { code: "AHL-FLDE-ZACH4015W40-1000", grade: "15W-40", pack: 1, unit: "L" },
    { code: "AHL-FLDE-ZCCH4015W40-015L", grade: "15W-40", pack: 15, unit: "L" },
    { code: "AHL-FLDE-ZCCH4015W40-018L", grade: "15W-40", pack: 18, unit: "L" },
    { code: "AHL-FLDE-ZCCH4015W40-8500", grade: "15W-40", pack: 8.5, unit: "L" },
    { code: "AHL-FLDE-ZCCH4015W40-6000", grade: "15W-40", pack: 6, unit: "L" },
    { code: "AHL-FLDE-ZDCH4015W40-055L", grade: "15W-40", pack: 55, unit: "L" },
    { code: "AHL-FLDE-ZDCH4020W40-055L", grade: "20W-40", pack: 55, unit: "L" },
    { code: "AHL-FLDE-ZDCF4015W40-210L", grade: "15W-40", pack: 210, unit: "L" },
    { code: "AHL-FLDE-ZDCH4015W40-210L", grade: "15W-40", pack: 210, unit: "L" },
    { code: "AHL-GODE-ZACF4020W40-1000", grade: "20W-40", pack: 1, unit: "L" },
    { code: "AHL-GODE-ZCCF4020W40-6000", grade: "20W-40", pack: 6, unit: "L" },
    { code: "AHL-GODE-ZCCF4020W40-7500", grade: "20W-40", pack: 7.5, unit: "L" },
    { code: "AHL-GODE-ZCCF4020W40-010L", grade: "20W-40", pack: 10, unit: "L" },
    { code: "AHL-GODE-ZCCF4020W40-8500", grade: "20W-40", pack: 8.5, unit: "L" },
    { code: "AHL-GODE-ZDCF4020W40-210L", grade: "20W-40", pack: 210, unit: "L" },
    { code: "AHL-GODE-ZBCF4015W40-3000", grade: "15W-40", pack: 3, unit: "L" },
  ],
  gear_oil: [
    { code: "AHL-TSGL-ZAGL4080W90-1000", grade: "80W-90", pack: 1, unit: "L" },
    { code: "AHL-TSGL-ZAGL4080W90-2500", grade: "80W-90", pack: 2.5, unit: "L" },
    { code: "AHL-TSGL-ZCGL4080W90-7000", grade: "80W-90", pack: 7, unit: "L" },
    { code: "AHL-TSGL-ZDGL4080W90-210L", grade: "80W-90", pack: 210, unit: "L" },
    { code: "AHL-TSGL-ZCGL4000090-026L", grade: "EP90", pack: 26, unit: "L" },
    { code: "AHL-TSGL-ZCGL4V00090-026L", grade: "EP90", pack: 26, unit: "L" },
    { code: "AHL-TSGL-ZBGL4000090-5000", grade: "EP90", pack: 5, unit: "L" },
    { code: "AHL-TSGL-ZDGL4000090-055L", grade: "EP90", pack: 55, unit: "L" },
    { code: "AHL-TSGL-ZDGL4000090-210L", grade: "EP90", pack: 210, unit: "L" },
    { code: "AHL-TSGL-ZCGL585W140-012L", grade: "85W-140", pack: 12, unit: "L" },
    { code: "AHL-TSGL-ZDGL585W140-210L", grade: "85W-140", pack: 210, unit: "L" },
    { code: "AHL-TSUT-ZBWET00080W-5000", grade: "80W", pack: 5, unit: "L" },
    { code: "AHL-TSUT-ZCWET00080W-020L", grade: "80W", pack: 20, unit: "L" },
    { code: "AHL-TSUT-ZCWETV0080W-020L", grade: "80W", pack: 20, unit: "L" },
    { code: "AHL-TSGL-ZACGL480W90-0120", grade: "80W-90", pack: 0.12, unit: "L" },
    { code: "AHL-TSGL-ZBGL4080W90-5000", grade: "80W-90", pack: 5, unit: "L" },
    { code: "AHL-TSGL-ZAGL4000090-1000", grade: "EP90", pack: 1, unit: "L" },
  ],
  automotive_fluid: [
    { code: "AHL-AFZ-ZABFDOT3-0100", grade: "DOT3", pack: 0.1, unit: "L" },
    { code: "AHL-AFZ-ZABFDOT3-0250", grade: "DOT3", pack: 0.25, unit: "L" },
    { code: "AHL-AFZ-ZA0BF000DOT4-0100", grade: "DOT4", pack: 0.1, unit: "L" },
    { code: "AHL-AFZ-ZA0BF000DOT4-0250", grade: "DOT4", pack: 0.25, unit: "L" },
    { code: "AHL-AFZ-ZB000ADBLUE-5000", name: "ADBLUE", pack: 5, unit: "L" },
    { code: "AHL-AFZ-ZC000ADBLUE-010L", name: "ADBLUE", pack: 10, unit: "L" },
    { code: "AHL-AFZ-ZC000ADBLUE-020L", name: "ADBLUE", pack: 20, unit: "L" },
    { code: "AHL-AFZ-ZD000ADBLUE-210L", name: "ADBLUE", pack: 210, unit: "L" },
    { code: "AHL-AFZ-ZD000ADBLUE-1000L", name: "ADBLUE", pack: 1000, unit: "L" },
    { code: "AHL-AFZ-ZDREFADBLUE-1000L", name: "ADBLUE Refill", pack: 1000, unit: "L" },
    { code: "AHL-AFZ-ZAOAT0000C17-1000", name: "Coolant 1:7", pack: 1, unit: "L" },
    { code: "AHL-AFZ-ZBOAT0000C17-3000", name: "Coolant 1:7", pack: 3, unit: "L" },
    { code: "AHL-AFZ-ZBOAT0000C17-5000", name: "Coolant 1:7", pack: 5, unit: "L" },
    { code: "AHL-AFZ-ZAOATANTIFRZ-1000", name: "Antifreeze Coolant", pack: 1, unit: "L" },
    { code: "AHL-AFZ-ZAOATWB00C14-1000", name: "WB Coolant 1:4", pack: 1, unit: "L" },
    { code: "AHL-AFZ-ZDOAT0000C11-210L", name: "Coolant 1:1", pack: 210, unit: "L" },
    { code: "AHL-AFZ-ZDHOAT000C13-210L", name: "Coolant 1:3", pack: 210, unit: "L" },
    { code: "AHL-AFZ-ZAHOAT000C13-1000", name: "Coolant 1:3", pack: 1, unit: "L" },
    { code: "AHL-AFZ-ZBHOAT000C13-3000", name: "Coolant 1:3", pack: 3, unit: "L" },
    { code: "AHL-AFZ-ZBHOAT000C13-5000", name: "Coolant 1:3", pack: 5, unit: "L" },
  ],
  hydraulic_oil: [
    { code: "AHL-HDF-ZC000000AW46-020L", name: "Hydraulic Oil AW 46", pack: 20, unit: "L" },
    { code: "AHL-HDF-ZC000000AW46-026L", name: "Hydraulic Oil AW 46", pack: 26, unit: "L" },
    { code: "AHL-HDF-ZD000000AW46-210L", name: "Hydraulic Oil AW 46", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZB000000AW68-5000", name: "Hydraulic Oil AW 68", pack: 5, unit: "L" },
    { code: "AHL-HDF-ZC000000AW68-020L", name: "Hydraulic Oil AW 68", pack: 20, unit: "L" },
    { code: "AHL-HDF-ZC000000AW68-026L", name: "Hydraulic Oil AW 68", pack: 26, unit: "L" },
    { code: "AHL-HDF-ZD000000AW68-210L", name: "Hydraulic Oil AW 68", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZD000000AW32-210L", name: "Hydraulic Oil AW 32", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZD00000HLP32-210L", name: "Hydraulic Oil HLP 32", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZC00000HLP46-020L", name: "Hydraulic Oil HLP 46", pack: 20, unit: "L" },
    { code: "AHL-HDF-ZC00000HLP46-026L", name: "Hydraulic Oil HLP 46", pack: 26, unit: "L" },
    { code: "AHL-HDF-ZD00000HLP46-210L", name: "Hydraulic Oil HLP 46", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZC00000HLP68-020L", name: "Hydraulic Oil HLP 68", pack: 20, unit: "L" },
    { code: "AHL-HDF-ZC00000HLP68-026L", name: "Hydraulic Oil HLP 68", pack: 26, unit: "L" },
    { code: "AHL-HDF-ZD00000HLP68-210L", name: "Hydraulic Oil HLP 68", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZD00ULTRA10W-210L", name: "Industrial Hydraulic Ultra 10W", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZD0000PHLP32-210L", name: "Hydraulic Oil Premium HLP 32", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZD0000PHLP46-210L", name: "Hydraulic Oil Premium HLP 46", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZD0000PHLP68-210L", name: "Hydraulic Oil Premium HLP 68", pack: 210, unit: "L" },
    { code: "AHL-HDF-ZD00AMPHLP46-210L", name: "Amaron Hydraulic Oil Premium HLP 46", pack: 210, unit: "L" },
  ],
  industrial_gear_oil: [
    { code: "AHL-ILGL-ZDGL00XF150-210L", name: "Industrial Gear Oil XF150", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00XF220-210L", name: "Industrial Gear Oil XF220", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00XF320-210L", name: "Industrial Gear Oil XF320", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00XF460-210L", name: "Industrial Gear Oil XF460", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00XF680-210L", name: "Industrial Gear Oil XF680", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00EP140-210L", name: "Industrial Gear Oil EP140", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00SP100-210L", name: "Industrial Gear Oil SP100", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00SP150-210L", name: "Industrial Gear Oil SP150", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00SP220-210L", name: "Industrial Gear Oil SP220", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZCGL0VSP220-020L", name: "Industrial Gear Oil SP220", pack: 20, unit: "L" },
    { code: "AHL-ILGL-ZCGL0VSP320-020L", name: "Industrial Gear Oil SP320", pack: 20, unit: "L" },
    { code: "AHL-ILGL-ZDGL0VSP220-210L", name: "Industrial Gear Oil SP220", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL0VSP320-210L", name: "Industrial Gear Oil SP320", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL0SP320E-210L", name: "Industrial Gear Oil SP320E", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL0VSP460-210L", name: "Industrial Gear Oil SP460", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00SP320-210L", name: "Industrial Gear Oil SP320", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00SP460-210L", name: "Industrial Gear Oil SP460", pack: 210, unit: "L" },
    { code: "AHL-ILGL-ZDGL00SP680-210L", name: "Industrial Gear Oil SP680", pack: 210, unit: "L" },
  ],
  industrial_grease: [
    { code: "AHL-IGRZ-ZDGRE0EP2HD-180K", name: "Industrial Grease EP 2 HD", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZDGREBBEP2-180K", name: "Industrial Grease BB EP2 HD", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZDGRE0EP0HD-180K", name: "Industrial Grease EP0 HD", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZDGREEP00HD-180K", name: "Industrial Grease EP00 HD", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZDGREP000HD-180K", name: "Industrial Grease EP000 HD", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZCGRE0EP2HD-018K", name: "Industrial Grease EP 2 HD", pack: 18, unit: "kg" },
    { code: "AHL-IGRZ-ZDGRE0EP3HD-180K", name: "Industrial Grease EP 3 HD", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZC00GRE0EP0-018K", name: "Industrial Grease EP 0", pack: 18, unit: "kg" },
    { code: "AHL-IGRZ-ZC0GRE0EP00-018K", name: "Industrial Grease EP 00", pack: 18, unit: "kg" },
    { code: "AHL-IGRZ-ZCGREBBEPX2-018K", name: "Industrial Grease BB EP 2", pack: 18, unit: "kg" },
    { code: "AHL-IGRZ-ZD00GRE0MP2-180K", name: "Industrial Grease MP2", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZD00GRE0EP2-180K", name: "Industrial Grease EP2", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZDLIXEP2BLU-180K", name: "Lithium Complex Blue Grease EP2", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZD00GRE0MP3-180K", name: "Industrial Grease MP3", pack: 180, unit: "kg" },
    { code: "AHL-IGRZ-ZC00GRE0MP3-018K", name: "Industrial Grease MP3", pack: 18, unit: "kg" },
  ],
  industrial_transmission: [
    { code: "AHL-ILTM-ZDTMC4SAE30-210L", name: "Industrial Transmission C4SAE30", pack: 210, unit: "L" },
    { code: "AHL-ILTM-ZDTMTO4SA50-210L", name: "Industrial Transmission TO4SAE50", pack: 210, unit: "L" },
  ],
  industrial_turbine: [
    { code: "AHL-ILTO-ZDTO00VG046-210L", name: "Industrial Turbine Oil PRIME46", pack: 210, unit: "L" },
  ],
  industrial_other: [
    { code: "AHL-ILTM-ZDTMTOP335U-210L", name: "Industrial Transformer Oil TOP335U", pack: 210, unit: "L" },
    { code: "AHL-AFZ-ZDEVCOLANT11-200L", name: "EV Coolant", pack: 200, unit: "L" },
    { code: "AHL-HTF-ZDTHERMIC500-210L", name: "Industrial Thermic Oil 500", pack: 210, unit: "L" },
    { code: "AHL-IFR-ZD0000FRHO46-200L", name: "Fire Resistance Hydraulic Oil 46", pack: 200, unit: "L" },
    { code: "AHL-MWF-ZD0SC00CUT0B-210L", name: "Industrial MWS Cutting Oil Cutb", pack: 210, unit: "L" },
    { code: "AHL-IBO-ZD10150TYPEC-210L", name: "Industrial Bearing Oil DIN51517-1 Typec", pack: 210, unit: "L" },
  ],
};
